
import { Component, OnInit, OnDestroy, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { KnowledgeMenuItem } from '../../services/my-knowledge.service';
import { ApiConfigService } from '../../services/api-config.service';

interface Slide {
  image: string;
  text1: string;
  text2: string;
  description: string;
  id: string;
}

@Component({
  selector: 'app-slider',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.css']
})
export class SliderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sliderItems: any[] = [];
  slides: Slide[] = [];
  displayedSlides: Slide[] = []; // For infinite scrolling
  currentIndex: number = 0;
  slidesPerPage: number = 3; // Default for desktop
  animationDuration: number = 1500; // 1.5 seconds
  autoplayInterval: number = 5000; // 5 seconds
  private autoplayTimer: any;
  private baseUrl = '';

  constructor(private router: Router, private apiConfigService: ApiConfigService) { 
    this.baseUrl = this.apiConfigService.getApiUrl('');
  }

  decode(text: string): string {
    const parser = new DOMParser();
    const decodedString = parser.parseFromString(`<!doctype html><body>${text}`, 'text/html').body.textContent;
    return decodedString || '';
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateSlidesPerPage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sliderItems'] && changes['sliderItems'].currentValue) {
      this.loadSlides();
    }
  }

  ngOnInit(): void {
    this.updateSlidesPerPage(); // Set initial slidesPerPage
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
  }

  loadSlides(): void {
    this.slides = this.sliderItems.map((item: any) => ({
      image: this.baseUrl + item.field_cover_image,
      text1: this.decode(item.title),
      text2: this.decode(item.field_short_description),
      description: item.field_description,
      id: item.nid
    }));
    this.setupInfiniteScroll();
    this.startAutoplay();
  }

  setupInfiniteScroll(): void {
    if (this.slides.length > 0) {
      // Duplicate slides for infinite scrolling effect
      this.displayedSlides = [...this.slides, ...this.slides, ...this.slides];
      // Start in the middle set of duplicated slides
      this.currentIndex = this.slides.length;
    }
  }

  startAutoplay(): void {
    this.stopAutoplay(); // Clear any existing timer
    this.autoplayTimer = setInterval(() => {
      this.nextSlide();
    }, this.autoplayInterval);
  }

  stopAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  nextSlide(): void {
    this.currentIndex += this.slidesPerPage;
    if (this.currentIndex >= this.slides.length * 2) {
      // If we are in the last duplicated set, jump back to the first duplicated set
      setTimeout(() => {
        this.currentIndex = this.slides.length;
        // Disable transition for instant jump
        const sliderWrapper = document.querySelector('.slider-wrapper') as HTMLElement;
        if (sliderWrapper) {
          sliderWrapper.style.transition = 'none';
        }
      }, this.animationDuration);
      setTimeout(() => {
        // Re-enable transition after jump
        const sliderWrapper = document.querySelector('.slider-wrapper') as HTMLElement;
        if (sliderWrapper) {
          sliderWrapper.style.transition = `transform ${this.animationDuration / 1000}s ease-in-out`;
        }
      }, this.animationDuration + 50);
    }
  }

  prevSlide(): void {
    this.currentIndex -= this.slidesPerPage;
    if (this.currentIndex < this.slides.length) {
      // If we are in the first duplicated set, jump back to the last duplicated set
      setTimeout(() => {
        this.currentIndex = this.slides.length * 2 - this.slidesPerPage;
        // Disable transition for instant jump
        const sliderWrapper = document.querySelector('.slider-wrapper') as HTMLElement;
        if (sliderWrapper) {
          sliderWrapper.style.transition = 'none';
        }
      }, this.animationDuration);
      setTimeout(() => {
        // Re-enable transition after jump
        const sliderWrapper = document.querySelector('.slider-wrapper') as HTMLElement;
        if (sliderWrapper) {
          sliderWrapper.style.transition = `transform ${this.animationDuration / 1000}s ease-in-out`;
        }
      }, this.animationDuration + 50);
    }
  }

  goToSlide(dotIndex: number): void {
    this.currentIndex = this.slides.length + (dotIndex * this.slidesPerPage);
  }

  get totalPages(): number {
    return Math.ceil(this.slides.length / this.slidesPerPage);
  }

  get currentDotIndex(): number {
    return Math.floor((this.currentIndex % this.slides.length) / this.slidesPerPage);
  }

  onMouseEnter(): void {
    this.stopAutoplay();
  }

  onMouseLeave(): void {
    this.startAutoplay();
  }

  onFocusIn(): void {
    this.stopAutoplay();
  }

  onFocusOut(): void {
    this.startAutoplay();
  }

  goToDetails(slide: Slide): void {
    this.router.navigate(['/my-knowledge-details', slide.id]);
  }

  // Method to update slidesPerPage based on screen size (will be called from host listener)
  updateSlidesPerPage(): void {
    if (window.innerWidth <= 768) { // Mobile
      this.slidesPerPage = 1;
    } else if (window.innerWidth <= 1024) { // Tablet
      this.slidesPerPage = 2;
    } else { // Desktop
      this.slidesPerPage = 3;
    }
  }
}
